import { Add, ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import PersonAdd from '@mui/icons-material/PersonAdd'
import {
  Avatar,
  Button,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import { Brand } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from 'tss-react/mui'

import manageBrandsPaths from '@src/components/brands/manageBrandsPaths'
import LinkChild from '@src/lib/LinkChild'
import { selectBrand } from '@src/stores/app/actions'
import { currentRetailerAccountAndBrandSlugSelector } from '@src/stores/app/selectors'
import { selectCurrentRetailerAccountBrandsMenuData } from '@src/stores/mainMenu/selectors'
import ReactComponent from '@src/svgs/logo-icon.svg?react'

import FundingChip from './FundingChip'

const BUTTON_LOGO_SIZE = 48

const useStyles = makeStyles()((theme) => ({
  container: {
    width: '70%',
    height: BUTTON_LOGO_SIZE,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  avatar: {
    width: BUTTON_LOGO_SIZE,
    height: BUTTON_LOGO_SIZE,
  },
  list: {
    '& .Mui-selected .MuiListItemText-primary': {
      fontWeight: 'bold',
    },
  },
  pbxxLogo: {
    height: BUTTON_LOGO_SIZE,
    marginRight: theme.spacing(2),
  },
}))

const SideNavBrandSelect: React.FC<React.PropsWithChildren> = () => {
  const { classes } = useStyles()
  const [areAllBrandsVisible, setAreAllBrandsVisible] = useState(false)
  const currentSlugs = useSelector(currentRetailerAccountAndBrandSlugSelector)

  const {
    brands,
  }: {
    brands: {
      id: null | Brand['id']
      name: Brand['name']
      logoUrl?: Brand['logo_url']
      slug: Brand['slug']
      walletBalanceCents: number
    }[]
  } = useSelector(selectCurrentRetailerAccountBrandsMenuData)

  const currentBrand = useMemo(() => {
    return brands.find((x) => x.slug === currentSlugs.brand)
  }, [brands, currentSlugs.brand])

  const dispatch = useDispatch()

  if (!currentBrand) {
    return null
  }

  function replaceBrandSlug(newBrandSlug: string) {
    const current = `${window.location.pathname}${window.location.search}`

    return current.replace(`/${currentSlugs.brand}`, `/${newBrandSlug}`)
  }

  const logoChild =
    currentBrand.id == null ? (
      <>
        <ReactComponent className={classes.pbxxLogo} />
        {currentBrand.name}
      </>
    ) : currentBrand.logoUrl == null ? (
      <Avatar className={classes.avatar}>
        {currentBrand.name?.slice(0, 1)}
      </Avatar>
    ) : (
      <img
        src={currentBrand.logoUrl}
        alt={`Logo for ${currentBrand.name}`}
        className={classes.logo}
      />
    )

  return (
    <>
      <Button
        onClick={() => setAreAllBrandsVisible((x) => !x)}
        fullWidth
        size="large"
        data-testid={'brandFilterDropdown'}
        endIcon={areAllBrandsVisible ? <ArrowDropUp /> : <ArrowDropDown />}
        color="inherit"
      >
        <div className={classes.container}>{logoChild}</div>
      </Button>
      <Collapse in={areAllBrandsVisible}>
        <List dense className={classes.list}>
          {brands.map((x) => {
            if (!x.slug) {
              return null
            }

            return (
              <LinkChild
                to={replaceBrandSlug(x.slug)}
                onClick={() => {
                  dispatch(
                    // TODO Simply changing the url seems to work without calling
                    // this, except for "all".
                    selectBrand({
                      id: x.id ? Number(x.id) : null,
                      skipUrlUpdate: true,
                    }),
                  )
                  setAreAllBrandsVisible(false)
                }}
                key={x.slug}
              >
                <ListItem
                  button
                  disableRipple
                  component="a"
                  data-testid={'brandFilterOption'}
                  selected={x.slug === currentSlugs.brand}
                >
                  <ListItemText primary={x.name} />
                  {x.walletBalanceCents > 0 ? (
                    <FundingChip
                      size="small"
                      amountInCents={x.walletBalanceCents}
                      variant="outlined"
                      color="secondary"
                    />
                  ) : undefined}
                </ListItem>
              </LinkChild>
            )
          })}

          <Divider />

          <LinkChild
            onClick={(e) => {
              setAreAllBrandsVisible(false)
            }}
            to={manageBrandsPaths.index.build({
              retailerAccountSlug: currentSlugs.retailerAccount,
              brandSlug: currentSlugs.brand,
              action: 'add',
            })}
          >
            <Button fullWidth startIcon={<PersonAdd />}>
              {I18n.t('manageBrands.addBrands')}
            </Button>
          </LinkChild>

          <Divider />

          <LinkChild
            onClick={(e) => {
              setAreAllBrandsVisible(false)
            }}
            to={manageBrandsPaths.index.build({
              retailerAccountSlug: currentSlugs.retailerAccount,
              brandSlug: currentSlugs.brand,
              action: 'suggest',
            })}
          >
            <Button fullWidth startIcon={<Add />}>
              {I18n.t('manageBrands.suggestBrands')}
            </Button>
          </LinkChild>

          <Divider />
        </List>
      </Collapse>
    </>
  )
}

export default SideNavBrandSelect
